import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  ChevronDown,
  Facebook,
  Instagram,
  Twitter,
  TrendingUp,
  Share2,
  Heart,
  Eye,
  Users,
  BarChart3
} from 'lucide-react';

import MetricCard from './MetricCard';
import CircularProgress from './CircularProgress';
import SocialMediaCard from './SocialMediaCard';
import InteractionChart from './InteractionChart';
import useAxiosInstance from '../../lib/useAxiosInstance';
import formatIndianNumber from '../../utils/formatIndianNumber';

function Result() {
    const axiosInstance = useAxiosInstance();
  const now = new Date();
//   const [selectedMonth, setSelectedMonth] = useState(now.toLocaleString('default', { month: 'long' }));
  const [selectedMonth, setSelectedMonth] = useState("July");
  const [selectedYear, setSelectedYear] = useState("2025");
//   const [selectedYear, setSelectedYear] = useState(now.getFullYear().toString());

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const years = ['2023', '2024', '2025'];

  // 🔹 Fetch analytics data (POST with body)
  const fetchAnalytics = async ({ queryKey }) => {
    const [_key, { year, month }] = queryKey;
    const monthIndex = months.indexOf(month) + 1; // Convert name → number (1–12)
    const response = await axiosInstance.post('/api/social-media/getSMAnalytics', {
      year: Number(year),
      month: monthIndex
    });
    return response.data;
  };

  const { data, isLoading, isError } = useQuery(
    ['social-media-analytics', { year: selectedYear, month: selectedMonth }],
    fetchAnalytics,
    {refetchOnWindowFocus: false,retry: 1,keepPreviousData: true }
  );
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen text-gray-600">
        Loading analytics...
      </div>
    );
  }

  if (isError || !data?.status) {
    return (
      <div className="flex justify-center items-center h-screen text-red-500">
        Failed to load analytics data.
      </div>
    );
  }

  const analytics = data.data || {};
  const totals = analytics.totals || {};
  const contentStats = analytics.contentStats || {};
  const followersStats = analytics.followersStats || {};
  const percentageChange = analytics.percentageChange || {};

  // Example mock for interaction chart (not in API)
  const interactionData = Array.from({ length: 22 }, (_, i) => ({
    date: i + 1,
    total: Math.floor(Math.random() * 5000) + 1000,
    postTotal: Math.floor(Math.random() * 4000) + 800
  }));

  return (
    <div className="min-h-screen">
      <div className="p-6 max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-black mb-2">Social Media Analytics</h1>
            <p className="text-gray-600">Track your performance across all platforms</p>
          </div>

          <div className="flex items-center gap-4">
            {/* Month Selector */}
            <div className="relative">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="appearance-none bg-gray-800/80 border border-gray-700 text-white px-4 py-2.5 pr-10 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 cursor-pointer hover:bg-gray-800 transition-colors"
              >
                {months.map(month => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Year Selector */}
            <div className="relative">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="appearance-none bg-gray-800/80 border border-gray-700 text-white px-4 py-2.5 pr-10 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 cursor-pointer hover:bg-gray-800 transition-colors"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Overview */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-black">Social Media Overview</h2>
            <div className="text-sm text-gray-600">
              Showing data for <span className="text-blue-400 font-medium">
                {months[analytics?.period?.month - 1]} {analytics?.period?.year}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <SocialMediaCard platform="Facebook" followers={followersStats.actualFollowers || 0} icon={Facebook} iconColor="text-[#1877F2]" />
            <SocialMediaCard platform="Instagram" followers={followersStats.actualFollowers || 0} icon={Instagram} iconColor="text-[#E4405F]" />
            <SocialMediaCard platform="Twitter" followers={followersStats.actualFollowers || 0} icon={Twitter} iconColor="text-[#1DA1F2]" />
          </div>
        </div>

        {/* Charts & Content Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2">
            <InteractionChart data={interactionData} growth={percentageChange.engagement || 0} />
          </div>
          <div className="card-wrapper !p-4 !rounded-2xl">
            <h3 className="text-lg font-semibold text-black mb-4">Content Stats</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between card-box card-box-sm !p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <BarChart3 className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-gray-500">Total Posts</span>
                </div>
                <span className="text-2xl font-bold text-black">{contentStats.totalPosts}</span>
              </div>
              <div className="flex items-center justify-between card-box card-box-sm !p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <BarChart3 className="w-5 h-5 text-purple-400" />
                  </div>
                  <span className="text-gray-600">Photos</span>
                </div>
                <span className="text-2xl font-bold text-black">{contentStats.photos}</span>
              </div>
              <div className="flex items-center justify-between card-box card-box-sm !p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-pink-500/20 rounded-lg">
                    <BarChart3 className="w-5 h-5 text-pink-400" />
                  </div>
                  <span className="text-gray-600">Videos</span>
                </div>
                <span className="text-2xl font-bold text-black">{contentStats.videos}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Metric & Circular Cards remain unchanged */}
        {/* ... rest of your UI as in your previous code ... */}
      </div>
        {/* Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          <MetricCard title="Engagement" value={formatIndianNumber(totals.engagement)} growth={percentageChange.engagement} icon={TrendingUp} iconBgColor="bg-purple-500/20" iconColor="text-purple-400" />
          <MetricCard title="Shares" value={formatIndianNumber(totals.shares)} growth={percentageChange.shares} icon={Share2} iconBgColor="bg-cyan-500/20" iconColor="text-cyan-400" />
          <MetricCard title="Reactions" value={formatIndianNumber(totals.reactions)} growth={percentageChange.reactions} icon={Heart} iconBgColor="bg-pink-500/20" iconColor="text-pink-400" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          <MetricCard title="Views" value={formatIndianNumber(totals.views)} growth={percentageChange.views} icon={Eye} iconBgColor="bg-blue-500/20" iconColor="text-blue-400" />
          <MetricCard title="Reach" value={formatIndianNumber(totals.reach)} growth={percentageChange.reach} icon={Users} iconBgColor="bg-emerald-500/20" iconColor="text-emerald-400" />
          <MetricCard title="Clicks" value={formatIndianNumber(totals.clicks)} growth={percentageChange.clicks} icon={BarChart3} iconBgColor="bg-orange-500/20" iconColor="text-orange-400" />
        </div>

        {/* Circular Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <CircularProgress value={formatIndianNumber(totals.engagement)} label="Total Engagement" color="text-cyan-400" growth={percentageChange.engagement} />
          <CircularProgress value={formatIndianNumber(totals.views)} label="Total Views" color="text-orange-400" growth={percentageChange.views} />
          <CircularProgress value={formatIndianNumber(totals.clicks)} label="Total Clicks" color="text-emerald-400" growth={percentageChange.clicks} />
        </div>

        {/* Followers Stats */}
        <div className="card-wrapper !p-4 !rounded-2xl">
          <h3 className="text-lg font-semibold text-black mb-6">Follower Statistics</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div className="text-center card-box card-box-sm !p-4">
              <div className="text-3xl font-bold text-black mb-2">
                {(followersStats.actualFollowers / 1000).toFixed(0)}K
              </div>
              <div className="text-sm text-gray-600">Actual Followers</div>
            </div>
            <div className="text-center card-box card-box-sm !p-4">
              <div className="text-3xl font-bold text-black mb-2">{formatIndianNumber(followersStats.followersThisYear)}</div>
              <div className="text-sm text-gray-600">This Year</div>
            </div>
            <div className="text-center card-box card-box-sm !p-4">
              <div className="text-3xl font-bold text-black mb-2">{formatIndianNumber(followersStats.followersThisMonth)}</div>
              <div className="text-sm text-gray-600">This Month</div>
            </div>
            <div className="text-center card-box card-box-sm !p-4">
              <div className="text-3xl font-bold text-black mb-2">{formatIndianNumber(followersStats.followersGainedLastMonth)}</div>
              <div className="text-sm text-gray-600">Followers Gained Last Month</div>
            </div>
            <div className="text-center p-4 bg-emerald-500/20 rounded-xl border border-emerald-500/30">
              <div className="text-3xl font-bold text-emerald-400 mb-2">
                {followersStats.gainedPercent || 0}%
              </div>
              <div className="text-sm text-gray-500">Followers Gained %</div>
            </div>
          </div>
        </div>
      </div>
  );
}

export default Result;
